import React, { forwardRef, InputHTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';
import clsx from 'clsx';

interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  onCheck?: (checked: boolean) => void;
}

export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ label, onCheck, checked, onChange, className, id, ...props }, ref) => {
    const switchId = id || `switch-${Math.random().toString(36).substr(2, 9)}`;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      onCheck?.(e.target.checked);
    };

    return (
      <div className={twMerge('flex items-center gap-3', className)}>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          onClick={() => {
            const newChecked = !checked;
            const event = {
              target: { checked: newChecked },
            } as React.ChangeEvent<HTMLInputElement>;
            handleChange(event);
          }}
          className={clsx(
            'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2',
            checked ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-700'
          )}
        >
          <span
            className={clsx(
              'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
              checked ? 'translate-x-6' : 'translate-x-1'
            )}
          />
        </button>
        <input
          ref={ref}
          type="checkbox"
          id={switchId}
          checked={checked}
          onChange={handleChange}
          className="sr-only"
          {...props}
        />
        {label && (
          <label
            htmlFor={switchId}
            className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);

Switch.displayName = 'Switch';
